import type { BoardGamePlayer, BoardGameTurn, BoardGameView } from './board-game-types';

/**
 * Why the rules refused a move, in the rules engine's names (`MoveRefusal`): the placement rules of
 * docs/game-rules.md section 3, then the turn and swap rules.
 */
export type BoardGameRefusalReason =
  | 'no-tiles'
  | 'out-of-bounds'
  | 'overlaps-hole'
  | 'collision'
  | 'multi-char-on-diagonal'
  | 'mixed-directions'
  | 'not-in-line'
  | 'gap-in-line'
  | 'misses-start'
  | 'no-word-formed'
  | 'not-connected'
  | 'not-a-word'
  | 'game-over'
  | 'not-your-turn'
  | 'bad-time'
  | 'not-in-rack'
  | 'repeated-tile'
  | 'bad-blank-letter'
  | 'swapping-off'
  | 'nothing-to-swap'
  | 'swap-over-limit'
  | 'swap-bigger-than-bag'
  | 'swap-needs-play-or-pass'
  | 'swap-ends-turn';

/** What the player reads for each refusal. */
export const REFUSAL_MESSAGES: Record<BoardGameRefusalReason, string> = {
  'no-tiles': 'Place at least one tile first.',
  'out-of-bounds': 'A tile is off the board.',
  'overlaps-hole': "A tile is on a square that isn't in play.",
  collision: 'Another tile is already there.',
  'multi-char-on-diagonal': "Multi-letter tiles can't run diagonally in this game.",
  'mixed-directions': 'Every multi-letter tile this turn must run the same way.',
  'not-in-line': 'Every tile this turn must sit on one line.',
  'gap-in-line': "There's a gap in the line of tiles.",
  'misses-start': 'The first word must cover the start square.',
  'no-word-formed': "That doesn't form a word of two or more letters.",
  'not-connected': 'The word must connect to a tile already on the board.',
  'not-a-word': "The dictionary doesn't have that word.",
  'game-over': 'The game has ended.',
  'not-your-turn': "It isn't your turn.",
  'bad-time': "That move couldn't be timed. Try again.",
  'not-in-rack': "A tile isn't on your rack any more.",
  'repeated-tile': 'A tile was used twice.',
  'bad-blank-letter': 'Choose the letters for each blank tile.',
  'swapping-off': "This game doesn't allow swaps.",
  'nothing-to-swap': 'Pick at least one tile to swap.',
  'swap-over-limit': 'That is more tiles than one swap allows.',
  'swap-bigger-than-bag': 'There are not that many tiles left in the bag.',
  'swap-needs-play-or-pass': 'In this game you swap, then play or pass.',
  'swap-ends-turn': 'In this game a swap is the whole turn.',
};

/** A refused move, as the server gave it. */
export interface BoardGameRefusal {
  reason: BoardGameRefusalReason;
  /** For `not-a-word`, the words the dictionary turned down. */
  words: string[];
}

/** What the player reads for a refusal, naming any words the dictionary turned down. */
export const refusalMessage = ({ reason, words }: BoardGameRefusal): string =>
  reason === 'not-a-word' && words.length > 0
    ? `Not in the dictionary: ${words.join(', ')}.`
    : (REFUSAL_MESSAGES[reason] ?? "That move isn't allowed.");

export const tilesLabel = (count: number): string => `${count} tile${count === 1 ? '' : 's'}`;

export const pointsLabel = (points: number): string => `${points} point${points === 1 ? '' : 's'}`;

/** The player's name in a sentence: "You" for the player, otherwise their name. */
const who = (player: BoardGamePlayer | undefined): string => (player?.isMe ? 'You' : (player?.name ?? 'Unknown'));

/** One line of the turn history, e.g. "Priya played CAT, AT for 12 points". */
export function turnLine(turn: BoardGameTurn, players: BoardGamePlayer[]): string {
  const name = who(players.find(({ seat }) => seat === turn.seat));
  const swapped = turn.swappedCount > 0 ? `swapped ${tilesLabel(turn.swappedCount)}` : '';
  if (turn.kind === 'swap') return `${name} ${swapped}`;
  if (turn.kind === 'pass') return swapped ? `${name} ${swapped}, then passed` : `${name} passed`;
  const played = `played ${turn.words.map(({ word }) => word).join(', ')} for ${pointsLabel(turn.points)}`;
  return swapped ? `${name} ${swapped}, then ${played}` : `${name} ${played}`;
}

const opponentOf = (game: Pick<BoardGameView, 'players'>) => game.players.find(({ isMe }) => !isMe);

const me = (game: Pick<BoardGameView, 'players'>) => game.players.find(({ isMe }) => isMe);

/** The game's result, from the player's side: "You won 312–280". */
export function resultLine(game: Pick<BoardGameView, 'players'>): string {
  const mine = me(game);
  const theirs = opponentOf(game);
  const score = `${mine?.score ?? 0}–${theirs?.score ?? 0}`;
  if (mine?.result === 'won') return `You won ${score}`;
  if (mine?.result === 'lost') return `${theirs?.name ?? 'Your opponent'} won ${score}`;
  return `A draw, ${score}`;
}

/** Why the game ended, e.g. "Priya played out" or "Four scoreless turns in a row". */
export function endLine(game: Pick<BoardGameView, 'endReason' | 'players' | 'turns'>): string | undefined {
  if (game.endReason === 'scoreless-turns') return 'Four scoreless turns in a row ended the game.';
  if (game.endReason !== 'went-out') return undefined;
  const last = game.turns.at(-1);
  const player = game.players.find(({ seat }) => seat === last?.seat);
  return player?.isMe ? 'You played your last tile.' : `${player?.name ?? 'Your opponent'} played their last tile.`;
}

/** Whose turn it is, or how the game stands, in a few words. */
export function statusLine(game: Pick<BoardGameView, 'status' | 'isMyTurn' | 'players'>): string {
  if (game.status === 'finished') return resultLine(game);
  if (game.status === 'cancelled') return 'This game was cancelled';
  if (game.status === 'waiting') return 'Waiting for an opponent to join';
  return game.isMyTurn ? 'Your turn' : `${opponentOf(game)?.name ?? 'Your opponent'}'s turn`;
}
